/**
 * InventoryPage
 * Kitchly v0.5.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  Package,
  Trash2,
  ShoppingCart,
  MapPin
} from 'lucide-react';
import { inventory, locations as locationsApi, shopping } from '../services/api';

function InventoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activeLocation, setActiveLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [invData, locData] = await Promise.all([
        inventory.list(activeLocation),
        locationsApi.list()
      ]);
      setItems(invData.items || []);
      setLocations(locData.locations || []);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [activeLocation, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleConsume = async (item) => {
    const itemName = item.custom_name || item.product_name || 'Item';
    try {
      const result = await inventory.consume(item.id);

      // Remove from local state immediately
      setItems(prev => prev.filter(i => i.id !== item.id));

      // Show undo toast
      toast((toastObj) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>{t('inventory.consumed', { name: itemName })}</span>
          <button
            onClick={async () => {
              try {
                await shopping.undoConsume({
                  shopping_list_item_id: result.shopping_list_item_id,
                  original_item: {
                    product_id: item.product_id,
                    location_id: item.location_id,
                    custom_name: item.custom_name,
                    quantity: item.quantity,
                    unit: item.unit,
                    notes: item.notes
                  }
                });
                toast.dismiss(toastObj.id);
                toast.success(t('inventory.undoSuccess'));
                loadData();
              } catch {
                toast.error(t('common.error'));
              }
            }}
            style={{
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '4px 12px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {t('inventory.undo')}
          </button>
        </div>
      ), { duration: 6000 });

    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleRemove = async (item) => {
    const itemName = item.custom_name || item.product_name || 'Item';
    try {
      await inventory.remove(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success(t('inventory.removed', { name: itemName }));
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return <div className="page-loader"><div className="loader-spinner" /></div>;
  }

  return (
    <div className="inventory-page">
      <div className="page-header">
        <h1>{t('inventory.title')}</h1>
        <div className="page-header-action">
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/add-item')}>
            <Plus size={18} /> {t('inventory.addItem')}
          </button>
        </div>
      </div>

      {/* Location filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <button
          className={`chip ${activeLocation === null ? 'active' : ''}`}
          onClick={() => { setActiveLocation(null); setLoading(true); }}
        >
          {t('inventory.allLocations')}
        </button>
        {locations.map(loc => (
          <button
            key={loc.id}
            className={`chip ${activeLocation === loc.id ? 'active' : ''}`}
            onClick={() => { setActiveLocation(loc.id); setLoading(true); }}
          >
            {loc.name}
            {loc.item_count > 0 && <span className="text-xs">({loc.item_count})</span>}
          </button>
        ))}
      </div>

      {/* Items list */}
      {items.length === 0 ? (
        <div className="empty-state">
          <Package size={64} className="empty-state-icon" />
          <h3>{activeLocation ? t('inventory.emptyLocation') : t('inventory.empty')}</h3>
          <p>{t('dashboard.noItems')}</p>
          <button className="btn btn-primary" onClick={() => navigate('/add-item')}>
            <Plus size={18} /> {t('inventory.addItem')}
          </button>
        </div>
      ) : (
        <div>
          {items.map(item => (
            <div key={item.id} className="list-item">
              <div
                className="list-item-icon"
                style={{
                  backgroundColor: item.location_color ? `${item.location_color}20` : 'var(--color-bg-input)',
                  color: item.location_color || 'var(--color-text-secondary)'
                }}
              >
                <Package size={20} />
              </div>

              <div className="list-item-content">
                <div className="list-item-title">
                  {item.custom_name || item.product_name || 'Unknown Item'}
                </div>
                <div className="list-item-subtitle">
                  {item.quantity} {item.unit}
                  {item.location_name && (
                    <span style={{ marginLeft: '8px' }}>
                      <MapPin size={12} style={{ verticalAlign: 'middle' }} /> {item.location_name}
                    </span>
                  )}
                </div>
              </div>

              <div className="list-item-actions">
                <button
                  className="btn-icon"
                  onClick={() => handleConsume(item)}
                  title={t('inventory.consume')}
                >
                  <ShoppingCart size={18} color="var(--color-accent)" />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleRemove(item)}
                  title={t('inventory.remove')}
                >
                  <Trash2 size={18} color="var(--color-error)" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InventoryPage;
